import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Grid,
  Paper,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Stack,
  Chip,
  Tooltip,
  Alert,
  Link as MuiLink
} from '@mui/material';
import {
  Close,
  NavigateBefore,
  NavigateNext,
  Add as AddIcon,
  Refresh,
  Edit,
  Share,
  ExpandMore,
  ExpandLess,
  Download,
  AspectRatio as AspectRatioIcon,
  AssignmentReturn as AssignmentReturnIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Image } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';

// APIサービス関連のモック（実際の実装では実際のAPIサービスを使用）
const ApiFetch = {
  API_PROTOCOL: "https:",
  API_HOST: "api.ourt-ai.work",
  IMG_HOST: "img.ourt-ai.work"
};

// モック：画像ファイルを取得する関数
const getAIGeneraleImageFile = async (procId) => {
  // 実際の実装では実際のAPIリクエストを行う
  return {
    result: true,
    data: {
      img: [`data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==`]
    }
  };
};

// モック：共有IDを作成する関数
const createShareId = async (procId) => {
  // 実際の実装では実際のAPIリクエストを行う
  return {
    result: true,
    data: {
      share_id: "abcdef123456"
    }
  };
};

// モック：ユーティリティ関数
const ImageUtil = {
  load: (_, file, __, callback) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result;
      callback(base64, base64, base64);
    };
    reader.readAsDataURL(file);
  }
};

// モック：Base64文字列をFileオブジェクトに変換
const base64ToFile = (base64String, fileName, mimeType) => {
  const byteString = atob(base64String);
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const int8Array = new Uint8Array(arrayBuffer);
  
  for (let i = 0; i < byteString.length; i++) {
    int8Array[i] = byteString.charCodeAt(i);
  }
  
  return new File([int8Array], fileName, { type: mimeType });
};

// 設定情報を表示するコンポーネント
const SettingsSection = ({ title, settings }) => (
  <Box>
    <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
      {title}
    </Typography>
    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
      <List dense disablePadding>
        {settings.map((setting, index) => (
          <ListItem key={index} disablePadding disableGutters sx={{ mb: 0.5 }}>
            <ListItemText 
              primary={
                <Box display="flex">
                  <Typography variant="body2" color="text.secondary" sx={{ width: 140, flexShrink: 0 }}>
                    {setting.name}
                  </Typography>
                  <Typography variant="body2">
                    {setting.value}
                  </Typography>
                </Box>
              } 
            />
          </ListItem>
        ))}
      </List>
    </Paper>
  </Box>
);

// キャラクタープロファイルカードのモックコンポーネント
const CharacterProfileCard = ({ data }) => (
  <Paper
    elevation={1}
    sx={{ p: 2, m: 1, maxWidth: 300, textAlign: 'center' }}
  >
    <Typography variant="h6">{data.character_name}</Typography>
    <Typography variant="body2">{data.character_name_kana}</Typography>
  </Paper>
);

// サムネイルコンポーネントのモック
const Thumb = ({ data }) => (
  <Paper
    elevation={1}
    sx={{ p: 2, m: 1, maxWidth: 300, textAlign: 'center' }}
  >
    <Typography variant="body1">作品ID: {data.group_id}</Typography>
    <Typography variant="body2">詳細ID: {data.id}</Typography>
  </Paper>
);

// メインダイアログコンポーネント
const ImageGenerationDialog = ({ 
  generatedInfo, 
  open, 
  onClose, 
  setShowGeneratedInfo, 
  setGeneratedInfo,
  generatedImageIndex,
  setGeneratedImageIndex,
  aiGenerateImageList,
  t,
  loginInfo,
  dialog,
  imageSet,
  imageAdd,
  setQualityTags,
  setSdSeed,
  setImg2ImgFile,
  setShowI2ICropper,
  setInpaintFile,
  setGenerateTabKey,
  setSelectedImageWidth,
  setSelectedImageHeight,
  setPreviewSource,
  setPreviewFlg
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  
  // State管理
  const [selectedImage, setSelectedImage] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // タブの切り替え
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // 前の画像に移動
  const handlePrevious = () => {
    if (generatedImageIndex > 0) {
      setGeneratedInfo(aiGenerateImageList[generatedImageIndex - 1]);
      setGeneratedImageIndex(generatedImageIndex - 1);
    }
  };

  // 次の画像に移動
  const handleNext = () => {
    if (generatedImageIndex < aiGenerateImageList.length - 1) {
      setGeneratedInfo(aiGenerateImageList[generatedImageIndex + 1]);
      setGeneratedImageIndex(generatedImageIndex + 1);
    }
  };

  // 基本設定と品質設定のパラメータを生成
  const basicSettings = [
    { name: 'モデル', value: generatedInfo.sd_model_checkpoint || 'N/A' },
    { name: '解像度', value: `${(generatedInfo.enable_hr === 1 ? generatedInfo.width * parseInt(generatedInfo.hr_scale || 1) : generatedInfo.width) * (generatedInfo.extra === 1 && generatedInfo.mode !== "extraBatchImages" ? 2 : 1)}x${(generatedInfo.enable_hr === 1 ? generatedInfo.height * parseInt(generatedInfo.hr_scale || 1) : generatedInfo.height) * (generatedInfo.extra === 1 && generatedInfo.mode !== "extraBatchImages" ? 2 : 1)}` },
    { name: 'モード', value: generatedInfo.mode === "extraBatchImages" ? "画像拡大" : generatedInfo.mode || 'N/A' },
    { name: 'サーバー', value: generatedInfo.server_id || 'N/A' }
  ];

  const qualitySettings = [
    { name: 'デノイジング強度', value: generatedInfo.denoising_strength || 'N/A' },
    { name: 'サンプラー', value: generatedInfo.sampler || 'N/A' },
    { name: 'シード', value: generatedInfo.real_seed === "" ? generatedInfo.seed : generatedInfo.real_seed || 'N/A' },
    { name: 'VAE', value: generatedInfo.sd_vae || 'N/A' }
  ];

  const advancedSettings = [
    { name: 'Hires fix', value: generatedInfo.enable_hr === 1 ? "Yes" : "No" },
    { name: 'Hires Upscaler', value: generatedInfo.enable_hr === 1 ? generatedInfo.hr_upscaler : "" },
    { name: 'inpaint', value: generatedInfo.inpaint === 1 ? "Yes" : "No" },
    { name: '画像拡大', value: generatedInfo.extra === 1 ? "Yes" : "No" },
    { name: '彩色', value: generatedInfo.controlnet_module === 'lineart_anime_denoise' ? "Yes" : "No" },
    { name: 'フル版', value: generatedInfo.full === 1 ? "Yes" : "No" }
  ];

  // 生成画像の配列を作成
  const generatedImages = Array.from({ length: generatedInfo.count || 1 }, (_, i) => ({
    id: i,
    url: `${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/sd/getImageSource/${generatedInfo.proc_id}/1/${i}`
  }));

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          height: fullScreen ? '100%' : '90vh',
          maxHeight: fullScreen ? '100%' : '90vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* ダイアログヘッダー */}
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: 'grey.100',
        borderBottom: 1,
        borderColor: 'divider',
        py: 1
      }}>
        <Typography variant="h6">画像生成結果</Typography>
        <Box>
          <IconButton 
            aria-label="前へ" 
            size="small" 
            sx={{ mr: 1 }}
            onClick={handlePrevious}
            disabled={generatedImageIndex <= 0}
          >
            <NavigateBefore />
          </IconButton>
          <IconButton 
            aria-label="次へ" 
            size="small" 
            sx={{ mr: 1 }}
            onClick={handleNext}
            disabled={generatedImageIndex >= (aiGenerateImageList.length - 1)}
          >
            <NavigateNext />
          </IconButton>
          <IconButton 
            aria-label="閉じる" 
            size="small" 
            onClick={() => setShowGeneratedInfo(false)}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      {/* ダイアログコンテンツ */}
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* 生成画像表示エリア */}
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
            生成画像
          </Typography>
          <Grid container spacing={2}>
            {Array.from({ length: generatedInfo.count || 1 }).map((_, i) => (
              <Grid item xs={generatedInfo.count > 1 ? 6 : 12} sm={generatedInfo.count > 1 ? 6 : 12} md={generatedInfo.count > 1 ? 6 : 12} key={i}>
                <Box sx={{ position: 'relative' }}>
                  {generatedInfo.count > 1 && (
                    <Chip
                      label={i + 1}
                      color="primary"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: '8px',
                        left: '8px',
                        zIndex: 1
                      }}
                    />
                  )}
                  <Paper
                    elevation={1}
                    sx={{
                      overflow: 'hidden',
                      borderRadius: 2,
                      cursor: 'pointer',
                      border: i === selectedImage ? `2px solid ${theme.palette.primary.main}` : 'none',
                    }}
                    onClick={() => {
                      setSelectedImage(i);
                      setPreviewSource(`${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/sd/getImageSource/${generatedInfo.proc_id}/1/${i}`);
                      setPreviewFlg(true);
                    }}
                  >
                    <Box
                      component="img"
                      src={`${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/sd/getImageSource/${generatedInfo.proc_id}/1/${i}`}
                      alt={`生成画像 ${i + 1}`}
                      sx={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        transition: '0.3s',
                        '&:hover': {
                          opacity: 0.9
                        }
                      }}
                    />
                  </Paper>
                </Box>
              </Grid>
            ))}
          </Grid>
          
          {generatedInfo.count > 1 && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange}
                variant="fullWidth"
                TabIndicatorProps={{ sx: { display: 'none' } }}
                sx={{ 
                  minHeight: 0,
                  '& .MuiTab-root': { 
                    minWidth: 0, 
                    minHeight: 0,
                    p: 0.5 
                  } 
                }}
              >
                {Array.from({ length: Math.ceil(generatedInfo.count / 4) }).map((_, i) => (
                  <Tab 
                    key={i}
                    icon={
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: tabValue === i ? 'primary.main' : 'grey.300',
                        }}
                      />
                    }
                    sx={{ mr: 1 }}
                  />
                ))}
              </Tabs>
            </Box>
          )}
        </Box>
        
        {/* サブスク未加入の場合のアラート */}
        {loginInfo.plan === "" && (
          <Box sx={{ p: 2 }}>
            <Link to="/legal" style={{ textDecoration: 'none' }}>
              <Alert severity="info">サブスクに加入してロゴを削除</Alert>
            </Link>
          </Box>
        )}
        
        {/* スクロール可能なコンテンツエリア */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* 生成日時 */}
          <Box sx={{ px: 3, pt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {generatedInfo.create_datetime}
            </Typography>
          </Box>
          
          {/* プロンプト情報 */}
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            {generatedInfo.random_src === "gacha" && generatedInfo.prompt_public === 0 ? (
              <Typography variant="body1" fontWeight="medium">プロンプト非公開</Typography>
            ) : (
              <>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  プロンプト情報
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-line' }}>{generatedInfo.prompt}</Typography>
                </Paper>
                <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                  ネガティブプロンプト
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-line' }}>{generatedInfo.negative_prompt}</Typography>
                </Paper>
              </>
            )}
          </Box>

          {/* 設定パラメータ */}
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <SettingsSection title="基本設定" settings={basicSettings} />
              </Grid>
              <Grid item xs={12} md={6}>
                <SettingsSection title="品質設定" settings={qualitySettings} />
              </Grid>
            </Grid>
          </Box>

          {/* 詳細設定の開閉ボタン */}
          <Button
            fullWidth
            color="primary"
            onClick={() => setShowAdvanced(!showAdvanced)}
            endIcon={showAdvanced ? <ExpandLess /> : <ExpandMore />}
            sx={{ 
              py: 1.5, 
              borderRadius: 0,
              justifyContent: 'center',
              borderBottom: 1,
              borderColor: 'divider'
            }}
          >
            {showAdvanced ? '詳細設定を隠す' : '詳細設定を表示'}
          </Button>

          {/* 詳細設定（折りたたみ可能） */}
          <Collapse in={showAdvanced}>
            <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
              <SettingsSection title="詳細設定" settings={advancedSettings} />
            </Box>
          </Collapse>

          {/* 参考元の作品/キャラクター情報 - 詳細設定の下に移動 */}
          {generatedInfo.src_group_id !== undefined && generatedInfo.src_group_id !== "" && (
            <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom sx={{ 
                borderBottom: '2px solid #555',
                paddingBottom: 1
              }}>
                参考元の{generatedInfo.random_src === "gacha" ? "キャラクター" : "作品"}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, mt: 2 }}>
                {generatedInfo.random_src === "gacha" ? (
                  <CharacterProfileCard 
                    data={{
                      group_id: generatedInfo.src_group_id,
                      user_id: "",
                      character_name: generatedInfo.character_name,
                      character_name_kana: generatedInfo.character_name_kana
                    }} 
                  />
                ) : (
                  <Thumb 
                    data={{
                      group_id: generatedInfo.src_group_id,
                      id: generatedInfo.src_detail_id,
                      favorite: generatedInfo.src_favorite
                    }} 
                    dialog={dialog} 
                  />
                )}
              </Box>
              <Typography variant="body1" align="center">{generatedInfo.src_title}</Typography>
            </Box>
          )}

          {/* 参考画像 */}
          {generatedInfo.mode !== undefined && generatedInfo.mode === "img2img" && (
            <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom sx={{ borderBottom: '2px solid #555' }}>
                参考画像
              </Typography>
              <Box
                component="img"
                src={`${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/sd/getImageBaseDirect/${generatedInfo.proc_id}`}
                alt="参考画像"
                sx={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  borderRadius: 2,
                  mb: 2
                }}
              />
            </Box>
          )}
        </Box>

        {/* アクションボタン */}
        <Box sx={{ 
          p: 2, 
          bgcolor: 'grey.50', 
          borderTop: 1, 
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}>
          {/* 主要アクションボタン */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />}
              onClick={(e) => {
                e.stopPropagation();
                getAIGeneraleImageFile(generatedInfo.proc_id).then(r => {
                  if(r.result){
                    const imgs = r.data.img.map(img => {
                      return base64ToFile(img.replace(/^.*,/, ''), generatedInfo.proc_id + ".png", "image/png");
                    });
                    if(fileList && fileList.length === 0){
                      imageSet(imgs, true, r.data);
                    } else {
                      imageAdd(imgs, true, r.data);
                    }
                    dialog(t("common.info"), "投稿画面の画像一覧に追加しました。左上の「閉じる」から投稿画面に移動できます。また、投稿画面の「生成履歴から選択」ボタンからも選択できます。");
                    setShowGeneratedInfo(false);
                  } else {
                    dialog("", r.errors);
                  }
                });
              }}
            >
              投稿
            </Button>

            <Tooltip title="生成情報を復元します。" arrow>
              <Button 
                variant="outlined"
                startIcon={<AssignmentReturnIcon />}
                onClick={() => {
                  document.querySelector("#sd-prompt").value = generatedInfo.prompt;
                  document.querySelector("#sd-negative-prompt").value = generatedInfo.negative_prompt;
                  if(generatedInfo.real_seed !== ""){
                    const seed = generatedInfo.real_seed.replace("[","").replace("]","").split(",")[selectedImage];
                    document.querySelector("#sd-seed").value = seed;
                    setSdSeed(seed);
                  }
                  setQualityTags(false);
                  dialog("確認","プロンプトを反映しました。");
                  setShowGeneratedInfo(false);
                }}
              >
                プロンプト復元 {generatedInfo.count > 1 ? `(${selectedImage + 1})` : ""}
              </Button>
            </Tooltip>

            <Tooltip title="参考画像に設定して、生成情報を復元します。" arrow>
              <Button 
                variant="outlined"
                startIcon={<AssignmentReturnIcon />}
                endIcon={<Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Icon.Image style={{ fontSize: '1rem' }} />
                </Box>}
                onClick={(e) => {
                  e.stopPropagation();
                  getAIGeneraleImageFile(generatedInfo.proc_id).then(r => {
                    if(r.result){
                      const extNo = selectedImage === 0 ? "" : "-" + (selectedImage + 1);
                      const base64 = r.data.img[selectedImage].replace(/^.*,/, '');
                      const file = base64ToFile(base64, generatedInfo.proc_id + extNo + ".png", "image/png");
                      ImageUtil.load(undefined, file, 0, (data1, data2, base64) => {
                        document.querySelector("#sd-prompt").value = generatedInfo.prompt;
                        document.querySelector("#sd-negative-prompt").value = generatedInfo.negative_prompt;
                        setQualityTags(false);

                        Array.from(document.getElementsByClassName("img2img-thumb")).forEach(e => e.src = data1);
                        setImg2ImgFile(base64);
                        setShowGeneratedInfo(false);
                        setShowI2ICropper(true);
                      });
                    }
                  });
                }}
              >
                参考画像設定 {generatedInfo.count > 1 ? `(${selectedImage + 1})` : ""}
              </Button>
            </Tooltip>

            <Tooltip title="画像を拡大します。" arrow>
              <Button 
                variant="outlined"
                startIcon={<AspectRatioIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  getAIGeneraleImageFile(generatedInfo.proc_id).then(r => {
                    if(r.result){
                      const extNo = selectedImage === 0 ? "" : "-" + (selectedImage + 1);
                      const base64 = r.data.img[selectedImage].replace(/^.*,/, '');
                      const file = base64ToFile(base64, generatedInfo.proc_id + extNo + ".png", "image/png");
                      ImageUtil.load(undefined, file, 0, (data1, data2, base64) => {
                        document.querySelector("#sd-prompt").value = generatedInfo.prompt;
                        document.querySelector("#sd-negative-prompt").value = generatedInfo.negative_prompt;
                        setQualityTags(false);

                        Array.from(document.getElementsByClassName("img2img-thumb")).forEach(e => e.src = data1);
                        setImg2ImgFile(base64);
                        setShowGeneratedInfo(false);
                        setShowI2ICropper(false);
                        setGenerateTabKey("extra");
                        
                        const img = document.createElement("img");
                        img.onload = () => {
                          setSelectedImageWidth(img.naturalWidth);
                          setSelectedImageHeight(img.naturalHeight);
                          img.remove();
                        };
                        img.src = base64;
                      });
                    }
                  });
                }}
              >
                画像拡大 {generatedInfo.count > 1 ? `(${selectedImage + 1})` : ""}
              </Button>
            </Tooltip>

            <Tooltip title="画像の一部を修正します(Inpaint)。" arrow>
              <Button 
                variant="outlined"
                startIcon={<Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Icon.Mask style={{ fontSize: '1rem' }} />
                </Box>}
                onClick={(e) => {
                  e.target.disabled = true;
                  e.stopPropagation();
                  getAIGeneraleImageFile(generatedInfo.proc_id).then(r => {
                    if(r.result){
                      const extNo = selectedImage === 0 ? "" : "-" + (selectedImage + 1);
                      const base64 = r.data.img[selectedImage].replace(/^.*,/, '');
                      const file = base64ToFile(base64, generatedInfo.proc_id + extNo + ".png", "image/png");
                      ImageUtil.load(undefined, file, 0, (data1, data2, base64) => {
                        setInpaintFile(base64);
                        setShowGeneratedInfo(false);
                        setShowI2ICropper(false);
                        setGenerateTabKey("extra");
                      });
                    }
                    e.target.disabled = false;
                  });
                }}
              >
                一部修正 {generatedInfo.count > 1 ? `(${selectedImage + 1})` : ""}
              </Button>
            </Tooltip>

            <Tooltip title="共有リンクをコピーします。" arrow>
              <Button 
                variant="outlined"
                startIcon={<Share />}
                onClick={() => {
                  createShareId(generatedInfo.proc_id).then(r => {
                    if(r.result){
                      navigator.clipboard.writeText(`https://ourt-ai.work/upload/image/generate?sid=${r.data.share_id}`);
                      dialog(t("common.info"), "共有リンクをクリップボードにコピーしました。");
                    } else {
                      dialog(t("common.error"), r.errors);
                    }
                    setShowGeneratedInfo(false);
                  });
                }}
              >
                共有
              </Button>
            </Tooltip>
          </Box>

          {/* ダウンロードボタン */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
            {Array.from({ length: generatedInfo.count || 1 }).map((_, i) => (
              <React.Fragment key={i}>
                {/* PNG ダウンロード */}
                {(loginInfo.plan !== "" || generatedInfo.use_coin > 0) ? (
                  <Button
                    variant="contained"
                    color="secondary"
                    component="a"
                    href={`${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/sd/getImageSource/${generatedInfo.proc_id}/3/${i}`}
                    download={`ourt-ai.work_${generatedInfo.proc_id}_${i+1}.png`}
                    startIcon={<Download />}
                    sx={{ minWidth: '120px' }}
                  >
                    PNG {generatedInfo.count > 1 ? `(${i+1})` : ""}
                  </Button>
                ) : (
                  <Tooltip title="サブスクプランに加入してPNGファイルをダウンロード" arrow>
                    <Button
                      variant="contained"
                      color="secondary"
                      component={Link}
                      to="/legal"
                      startIcon={<Download />}
                      sx={{ opacity: 0.7, minWidth: '120px' }}
                    >
                      PNG {generatedInfo.count > 1 ? `(${i+1})` : ""}
                    </Button>
                  </Tooltip>
                )}

                {/* JPG ダウンロード - すべてのユーザーが利用可能 */}
                <Button
                  variant="contained"
                  color="secondary"
                  component="a"
                  href={`${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/sd/getImageSource/${generatedInfo.proc_id}/0/${i}`}
                  download={`ourt-ai.work_${generatedInfo.proc_id}_${i+1}.jpg`}
                  startIcon={<Download />}
                  sx={{ minWidth: '120px' }}
                >
                  JPG {generatedInfo.count > 1 ? `(${i+1})` : ""}
                </Button>
              </React.Fragment>
            ))}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// 使用例
const ImageGenerationDialogTest = () => {
  // モックデータ
  const [showGeneratedInfo, setShowGeneratedInfo] = useState(true);
  const [generatedImageIndex, setGeneratedImageIndex] = useState(0);
  const [generatedInfo, setGeneratedInfo] = useState({
    proc_id: '123456789',
    count: 4,
    prompt: 'A pretty Japanese woman with dark hair is standing and looking at you. Delicate expression.',
    negative_prompt: 'low quality, bad anatomy, worst quality',
    steps: 30,
    cfg_scale: 7,
    denoising_strength: 0.75,
    seed: '1234567890',
    real_seed: '',
    sampler: 'Euler a',
    enable_hr: 1,
    hr_upscaler: 'Latent',
    width: 512,
    height: 512,
    hr_scale: 2,
    extra: 0,
    mode: 'img2img',
    sd_model_checkpoint: 'imagen-3.0',
    sd_vae: 'default',
    inpaint: 0,
    controlnet_module: '',
    full: 0,
    server_id: 4,
    create_datetime: '2023-05-01 12:34:56',
    prompt_public: 1,
    random_src: 'gacha',
    src_group_id: 'G12345',
    src_detail_id: 'D67890',
    src_favorite: 0,
    character_name: 'サンプルキャラクター',
    character_name_kana: 'さんぷるきゃらくたー',
    src_title: 'サンプル作品タイトル'
  });
  
  const [aiGenerateImageList, setAiGenerateImageList] = useState([
    { ...generatedInfo, proc_id: '123456789' },
    { ...generatedInfo, proc_id: '987654321', prompt: '別のプロンプト例' }
  ]);
  
  // モック関数
  const t = (key) => key === 'common.info' ? '情報' : 
                    key === 'common.error' ? 'エラー' : 
                    key === 'common.prev' ? '前へ' : 
                    key === 'common.next' ? '次へ' : key;
                    
  const loginInfo = { plan: "" }; // サブスク未加入ユーザー
  
  const dialog = (title, message) => {
    console.log(`${title}: ${message}`);
    alert(`${title}: ${message}`);
  };
  
  const fileList = [];
  
  const imageSet = (imgs, flag, data) => {
    console.log('画像を設定:', imgs, flag, data);
  };
  
  const imageAdd = (imgs, flag, data) => {
    console.log('画像を追加:', imgs, flag, data);
  };
  
  const setQualityTags = (value) => {
    console.log('品質タグを設定:', value);
  };
  
  const setSdSeed = (value) => {
    console.log('シード値を設定:', value);
  };
  
  const setImg2ImgFile = (value) => {
    console.log('img2imgファイルを設定:', value);
  };
  
  const setShowI2ICropper = (value) => {
    console.log('I2Iクロッパーを表示:', value);
  };
  
  const setInpaintFile = (value) => {
    console.log('インペイントファイルを設定:', value);
  };
  
  const setGenerateTabKey = (value) => {
    console.log('生成タブキーを設定:', value);
  };
  
  const setSelectedImageWidth = (value) => {
    console.log('選択画像の幅を設定:', value);
  };
  
  const setSelectedImageHeight = (value) => {
    console.log('選択画像の高さを設定:', value);
  };
  
  const setPreviewSource = (value) => {
    console.log('プレビューソースを設定:', value);
  };
  
  const setPreviewFlg = (value) => {
    console.log('プレビューフラグを設定:', value);
  };
  
  return (
    <div>
      <Button variant="contained" onClick={() => setShowGeneratedInfo(true)}>
        ダイアログを開く
      </Button>
      
      <ImageGenerationDialog 
        open={showGeneratedInfo}
        onClose={() => setShowGeneratedInfo(false)}
        generatedInfo={generatedInfo}
        setShowGeneratedInfo={setShowGeneratedInfo}
        setGeneratedInfo={setGeneratedInfo}
        generatedImageIndex={generatedImageIndex}
        setGeneratedImageIndex={setGeneratedImageIndex}
        aiGenerateImageList={aiGenerateImageList}
        t={t}
        loginInfo={loginInfo}
        dialog={dialog}
        imageSet={imageSet}
        imageAdd={imageAdd}
        setQualityTags={setQualityTags}
        setSdSeed={setSdSeed}
        setImg2ImgFile={setImg2ImgFile}
        setShowI2ICropper={setShowI2ICropper}
        setInpaintFile={setInpaintFile}
        setGenerateTabKey={setGenerateTabKey}
        setSelectedImageWidth={setSelectedImageWidth}
        setSelectedImageHeight={setSelectedImageHeight}
        setPreviewSource={setPreviewSource}
        setPreviewFlg={setPreviewFlg}
      />
    </div>
  );
};

export default ImageGenerationDialogTest;